import {useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {toQueryString} from '@s-ui/js/lib/string'
import {useDecision} from '@s-ui/pde'
import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import FormBuilder, {
  checkConstraintsFactory,
  formBuilderChangeFieldById,
  formBuilderPickFieldById
} from '@s-ui/react-form-builder'
import {useRouter} from '@s-ui/react-router'

import {useUserProfileEditTracking} from '../../hooks/useUserProfileEditTracking/index.js'
import {useFormUserPersonalDataConfiguration} from './useFormUserPersonalDataConfiguration.js'

const BASE_CLASS = 'mt-userProfileEdit-formUserPersonalData'
const NO_OP = () => {}
const FormUserPersonalData = ({
  onClickModifyPassword = NO_OP,
  onErrorUpdatingUser = NO_OP,
  onSuccessUpdatingUser = NO_OP,
  userData
}) => {
  const {domain, i18n} = useContext(Context)
  const router = useRouter()

  const config = domain.get('config')
  const spainPostalCodeRegex = config.get('REGEX_SPAIN_POSTALCODE')
  const {USER_PHONE_VERIFICATION_PATTERN, USER_PROFILE_PATTERN} = config.get('page')
  const {USER_PHONE_VERIFICATION_ENABLED} = config.get('user')

  // ff_web_seller_sms_user_phone_verification
  const {enabled: isWithPhoneVerificationFeatureFlag} = useDecision('ff_web_seller_sms_user_phone_verification')

  const isWithPhoneVerification = USER_PHONE_VERIFICATION_ENABLED && isWithPhoneVerificationFeatureFlag

  const {
    formFieldIds,
    formFields,
    formUserPersonalDataConfiguration,
    initFields,
    numericFieldsToBeFormatted,
    SUIComponentsConfig
  } = useFormUserPersonalDataConfiguration({userData})

  const {trackError, trackSuccess} = useUserProfileEditTracking()

  const [fields, setFields] = useState(initFields)
  const [isLoading, setIsLoading] = useState(false)
  const [validatedErrors, setValidatedErrors] = useState({})

  const contactPhoneRegex = /^(\+34|0034|34)?[6|7|8|9][0-9]{8}$/

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formUserPersonalDataConfiguration)(checkParams)

    const {value: phoneFieldValue} = formBuilderPickFieldById(formFields, formFieldIds.phone)

    if (phoneFieldValue && !contactPhoneRegex.test(phoneFieldValue)) {
      _errors.phone = [i18n.t('INSTANT_OFFER.LANDING.PHONE_NOT_VALID')]
    }

    const {value: postalCodeFieldValue} = formBuilderPickFieldById(formFields, formFieldIds.postalCode)

    if (postalCodeFieldValue && !spainPostalCodeRegex.test(postalCodeFieldValue))
      _errors.postalCode = [i18n.t('MODAL_LAST_CALL.VALIDATION_MESSAGES.POSTCODE_INVALID')]

    setErrors(_errors)
    return _errors
  }

  const handleOnBlur = fieldId => {
    checkConstraints({fieldId})
  }

  const handleOnChange = newFields => {
    const fieldChanged = newFields.__FIELD_CHANGED__

    if (numericFieldsToBeFormatted.includes(fieldChanged)) {
      const formattedValue = newFields[fieldChanged].replace(/[^0-9]/g, '')
      newFields[fieldChanged] = formattedValue
      formBuilderChangeFieldById(formFields, fieldChanged, {
        value: formattedValue
      })
    }

    if (fieldChanged === formFieldIds.postalCode) {
      const {value} = formBuilderPickFieldById(formFields, fieldChanged)
      if (value.length > 5) {
        formBuilderChangeFieldById(formFields, formFieldIds.postalCode, {
          value: value.slice(0, 5)
        })
      }
    }

    setFields({...newFields})
  }

  const checkIsUserPhoneVerified = () => {
    return domain.get('check_phone_verification_status_user_use_case').execute({phone: fields.phone})
  }

  const updateUserData = async () => {
    const {hidePhone, lastName, name, phone, postalCode} = fields

    const [error] = await domain.get('update_data_user_use_case').execute({
      useNewEndpoint: true,
      userData: {
        hidePhone,
        lastName,
        name,
        phone,
        zipCode: postalCode
      }
    })

    if (error) {
      const isWrongPostalCodeError = error.isWrongPostalCodeError?.() ?? false
      if (isWrongPostalCodeError) {
        setErrors({postalCode: [i18n.t('MODAL_LAST_CALL.VALIDATION_MESSAGES.POSTCODE_INVALID')]})
        trackError({
          formErrors: {[formFieldIds.postalCode]: [i18n.t('MODAL_LAST_CALL.VALIDATION_MESSAGES.POSTCODE_INVALID')]}
        })
      } else {
        trackError({
          formErrors: {[formFieldIds.phone]: [i18n.t('FORM.DUPLICATE_PHONE_TEXT_ERROR')]}
        })
      }
      onErrorUpdatingUser({error})
    } else {
      trackSuccess({fields: {lastName, name, phone, postalCode}})
      onSuccessUpdatingUser()
    }

    setIsLoading(false)
  }

  const handleOnSubmit = async () => {
    setIsLoading(true)

    const errors = checkConstraints()

    const isValidForm = errors => !Object.values(errors).some(error => error.length > 0)

    if (!isValidForm(errors)) {
      setIsLoading(false)
      onErrorUpdatingUser()
      trackError({
        formErrors: errors
      })
    } else {
      if (!isWithPhoneVerification) {
        updateUserData()
        return
      } else {
        const [error, isUserPhoneVerified] = await checkIsUserPhoneVerified()
        if (error) {
          trackError()
          return
        }
        if (isUserPhoneVerified.isVerified === true) {
          updateUserData()
          return
        }

        const {lastName, name, phone, postalCode} = fields
        const queryString = toQueryString({
          editingProfilePending: true,
          phone,
          name,
          lastName,
          postalCode
        })

        router.push(`${USER_PHONE_VERIFICATION_PATTERN}?${queryString}`)
      }
    }

    setIsLoading(false)
  }

  const handleGoBackClick = () => {
    router.push(USER_PROFILE_PATTERN)
  }

  const handleModifyPasswordClick = () => {
    onClickModifyPassword()
  }

  return (
    <div className={BASE_CLASS}>
      <h2 className={`${BASE_CLASS}-title`}>{i18n.t('USER_PROFILE.EDIT.PERSONAL_DATA_TITLE')}</h2>
      <div className={`${BASE_CLASS}-form`}>
        <FormBuilder
          initFields={initFields}
          json={formUserPersonalDataConfiguration}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          errors={validatedErrors}
          setErrors={setValidatedErrors}
        >
          {SUIComponentsConfig}
        </FormBuilder>
      </div>
      <h2 className={`${BASE_CLASS}-title ${BASE_CLASS}-title--smallMargin`}>
        {i18n.t('USER_PROFILE.EDIT.LOGIN_SECTION_TITLE')}
      </h2>
      <AtomButton design={atomButtonDesigns.LINK} onClick={handleModifyPasswordClick} size={atomButtonSizes.MEDIUM}>
        {i18n.t('COMMON.MODIFY_PASSWORD')}
      </AtomButton>
      <div className={`${BASE_CLASS}-buttons`}>
        <AtomButton
          shape={atomButtonShapes.CIRCULAR}
          onClick={handleOnSubmit}
          isLoading={isLoading}
          size={atomButtonSizes.LARGE}
        >
          {i18n.t('COMMON.SAVE_CHANGES')}
        </AtomButton>
        <AtomButton
          design={atomButtonDesigns.FLAT}
          onClick={handleGoBackClick}
          size={atomButtonSizes.MEDIUM}
          shape={atomButtonShapes.CIRCULAR}
        >
          {i18n.t('COMMON.CANCEL')}
        </AtomButton>
      </div>
    </div>
  )
}

FormUserPersonalData.displayName = 'FormUserPersonalData'

FormUserPersonalData.propTypes = {
  onClickModifyPassword: PropTypes.func,
  onErrorUpdatingUser: PropTypes.func,
  onSuccessUpdatingUser: PropTypes.func,
  userData: PropTypes.object
}

export default FormUserPersonalData
